import Icon from '@ant-design/icons';
import { ConsentStatusType, Entitlement, SearchParams } from '@api/constants';
import { revokeConsent } from '@api/selfServiceServices';
import { AssuranceType, DocumentPurpose, FactorType } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import {
  getDaysUntilExpiration,
  PASSWORD_EXPIRATION_WARNING_DAYS,
} from '@common/utils/passwordExpiration';
import { isDismissed, markDismissed, SessionStorageKeys } from '@common/utils/sessionStorage';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { Button, ImprList, Modal } from '@imprivata-cloud/components';
import { Alert, Button as ImprButton } from '@imprivata-cloud/design-system';
import {
  useCredentialDelete,
  useUnenrollAuthenticator,
} from '@portal/containers/users/hooks/useAuthenticators';
import { useMfaEnrollmentStatus } from '@portal/hooks/useMfaEnrollmentStatus';
import { sleep } from '@utils/utils';
import type { ColumnType } from 'antd/es/table';
import { CircleHelp, Clock, type LucideIcon, TriangleAlert, X } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { featureSupported } from '@/featureFlagsUtils';
import { RootRoutes } from '@/index';
import { usePasswordExpirationInfo } from '@/portal/hooks/usePasswordExpirationInfo';
import FullPageError from '../../assets/full_page_error.svg?react';
import FaceIcon from '../../assets/icons/authenticators/face_bio.svg?react';
import imprivataIdIcon from '../../assets/icons/authenticators/imprivata_ID.svg?react';
import OtpIcon from '../../assets/icons/authenticators/otp.svg?react';
import PasswordIcon from '../../assets/icons/authenticators/password.svg?react';
import EmptyImage from '../../assets/self-service-authenticator-list-empty-table.svg?react';
import getRelativeTime from '../../common/authMethodInfo/relativeTime';
import AuthenticatorActionItems from '../../components/authenticatorActionItems/AuthenticatorActionItems';
import AuthenticatorNameAndIcon from '../../components/authenticatorNameAndIcon/AuthenticatorNameAndIcon';
import { SelfServicePath } from '../SelfServiceRoutes';
import { isBioReconsentDue } from '../utils/bioConsent';
import { isFaceEnabled } from '../utils/helpers';
import { canEnrollOtp } from '../utils/otpUtils';
import { useHasCamera } from '../utils/useHasCamera';
import { useSelfServiceData } from '../utils/useSelfServiceData';
import AuthMethodListHeader, {
  type AuthenticatorUiData,
} from './AuthMetodListHeader/AuthMethodListHeader';

import './AuthenticatorList.less';

const TRANSLATION_ROOT = 'self-service.authenticator-list';
const TRANSLATION_ROOT_PASS = 'portal.landing.password-expiration-banner';

// Every icon here but the name column's authenticator SVGs is lucide, so size is
// a prop rather than CSS.
const ROW_WARNING_ICON_SIZE = 16;
const MODAL_GRAPHIC_SIZE = 48;
const MODAL_CLOSE_ICON_SIZE = 20;

// antd takes the width as a prop (it lands on the panel's inline style); the rest
// of the frame is in the stylesheet.
const CONFIRMATION_MODAL_WIDTH = 395;

// Named rather than bare literals: each kind is written at the union, the
// presentation table and the row derivation, where a typo would silently render
// no warning instead of failing.
const ROW_WARNING = {
  // S2068 fires on any literal under a `password`-named key. These name a warning
  // about a password, they do not hold one.
  passwordExpired: 'password-expired', // NOSONAR
  passwordExpiring: 'password-expiring', // NOSONAR
  updateResidence: 'update-residence',
} as const;

type RowWarning =
  | { kind: typeof ROW_WARNING.passwordExpired }
  | { kind: typeof ROW_WARNING.passwordExpiring; daysLeft: number }
  | { kind: typeof ROW_WARNING.updateResidence };

// One class per severity, not per kind, which is what keeps the residence and
// password-expiry warnings looking alike. The `password-` names predate the
// residence warning reusing them.
const ROW_WARNING_CLASS = { error: 'password-expired', warning: 'password-warning' } as const;

// Two separate scales: a row warning picks a text colour, a modal graphic an icon
// colour.
const SEVERITY = { error: 'error', warning: 'warning' } as const;
const GRAPHIC_SEVERITY = { neutral: 'neutral', warning: 'warning' } as const;

// Every variant is the same object — icon, coloured bold text — so what varies is
// tabulated here instead of spread across conditionals in the component.
const ROW_WARNING_PRESENTATION: Record<
  RowWarning['kind'],
  { severity: keyof typeof ROW_WARNING_CLASS; icon: LucideIcon; messageKey: string }
> = {
  // Only this kind's message key matches its name, hence tabulated rather than
  // derived.
  [ROW_WARNING.passwordExpired]: {
    severity: SEVERITY.error,
    icon: Clock,
    messageKey: ROW_WARNING.passwordExpired,
  },
  [ROW_WARNING.passwordExpiring]: {
    severity: SEVERITY.warning,
    icon: Clock,
    messageKey: 'password-expires-in',
  },
  [ROW_WARNING.updateResidence]: {
    severity: SEVERITY.warning,
    icon: TriangleAlert,
    messageKey: 'update-residence-warning',
  },
};

const RowWarningMessage = ({ warning }: { warning?: RowWarning }) => {
  const { t } = useTranslation();

  if (!warning) {
    return null;
  }

  // Not `WarningIcon` → `Icon`: that name belongs to the antd wrapper imported
  // above.
  const { severity, icon: WarningIcon, messageKey } = ROW_WARNING_PRESENTATION[warning.kind];

  return (
    <span className={ROW_WARNING_CLASS[severity]}>
      <WarningIcon size={ROW_WARNING_ICON_SIZE} aria-hidden="true" />
      {/* Only the expiring message interpolates a day count. */}
      {t(
        `${TRANSLATION_ROOT}.${messageKey}`,
        warning.kind === ROW_WARNING.passwordExpiring ? { count: warning.daysLeft } : undefined,
      )}
    </span>
  );
};

// All three modals on this page are the same object — graphic, title, one line of
// copy, confirm/dismiss pair — so the frame is declared once here. The graphic is
// the icon component rather than rendered JSX, so its size stays the frame's.
// `okPrimary` exists because the destructive confirms make Cancel primary, and the
// primary button stays on the left either way.
type ConfirmationModalProps = {
  open: boolean;
  onCancel: () => void;
  onOk?: () => void;
  graphic: LucideIcon;
  graphicSeverity?: (typeof GRAPHIC_SEVERITY)[keyof typeof GRAPHIC_SEVERITY];
  title: ReactNode;
  content: ReactNode;
  okText: string;
  cancelText: string;
  okPrimary?: boolean;
  okLoading?: boolean;
};

const ConfirmationModal = ({
  open,
  onCancel,
  onOk,
  graphic: Graphic,
  graphicSeverity = GRAPHIC_SEVERITY.neutral,
  title,
  content,
  okText,
  cancelText,
  okPrimary = false,
  okLoading = false,
}: ConfirmationModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      okButtonProps={{ type: okPrimary ? 'primary' : 'default', loading: okLoading }}
      cancelButtonProps={{ type: okPrimary ? 'default' : 'primary' }}
      // The confirm sits left of the dismiss, swapped here rather than reversed in
      // CSS so DOM and tab order match the screen.
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <OkBtn />
          <CancelBtn />
        </>
      )}
      // Dismisses exactly like Cancel — it never confirms.
      closable={{ 'aria-label': t('common.close') }}
      closeIcon={<X size={MODAL_CLOSE_ICON_SIZE} aria-hidden="true" />}
      width={CONFIRMATION_MODAL_WIDTH}
      centered={true}
      className="self-service-confirmation-modal"
      maskClosable={false}
      // antd default; returns focus to the triggering control on close.
      focusTriggerAfterClose
    >
      <Graphic
        className={
          graphicSeverity === GRAPHIC_SEVERITY.warning ? 'modal-graphic warning' : 'modal-graphic'
        }
        size={MODAL_GRAPHIC_SIZE}
        aria-hidden="true"
      />
      <h2>{title}</h2>
      <p>{content}</p>
    </Modal>
  );
};

const columns: ColumnType[] = [
  {
    dataIndex: 'name-320',
    key: 'name-320',
    responsive: ['xs'],
    render: (_, record) => (
      <>
        <AuthenticatorNameAndIcon factorType={record.factorType} />
        {record.additionalText && (
          <span style={{ color: 'var(--grey-60)', paddingTop: '9px' }}>
            {record.additionalText}
          </span>
        )}
      </>
    ),
  },
  {
    dataIndex: 'name',
    key: 'name',
    responsive: ['sm'],
    render: (_, record) => <AuthenticatorNameAndIcon factorType={record.factorType} />,
  },
  {
    dataIndex: 'shortName',
    key: 'shortName',
    responsive: ['sm'],
    render: (_, record) =>
      record.factorType === FactorType.IMPRIVATA_ID && (
        <div>
          {record.shortName}
          {record.deviceModel && ` (${record.deviceModel})`}
        </div>
      ),
  },
  {
    dataIndex: 'additionalText',
    key: 'additionalText',
    responsive: ['sm'],
    render: (_, record) =>
      record.additionalText && (
        <span style={{ color: 'var(--grey-60)' }}>{record.additionalText}</span>
      ),
  },
  {
    dataIndex: 'rowWarning',
    key: 'rowWarning',
    responsive: ['sm'],
    render: (_, record) => <RowWarningMessage warning={record.rowWarning} />,
  },
  {
    dataIndex: 'action',
    key: 'action',
    render: (_, record) => (
      <AuthenticatorActionItems
        key={record.key}
        factorType={record.factorType}
        onRemove={record.onRemove}
        onRevokeConsent={record?.onRevokeConsent}
        onEdit={record?.onEdit}
        epcs={record?.epcs}
        onEpcsSetup={record?.onEpcsSetup}
        onUpdateBioAgreement={record?.onUpdateBioAgreement}
      />
    ),
  },
];

interface AuthenticatorToRemove {
  factorType: FactorType;
  remove: () => void;
}

const supportedFactors = new Set([
  FactorType.FACE,
  FactorType.PROX,
  FactorType.IMPRIVATA_ID,
  FactorType.PASSWORD,
  FactorType.OTP,
]);

const REVOCABLE_CONSENT_STATUSES: ReadonlySet<ConsentStatusType> = new Set([
  ConsentStatusType.VALID,
  ConsentStatusType.EXPIRED,
]);

const ENABLE_EPCS = featureSupported('ENABLE_EPCS');
const ENABLE_EMAIL_OTP = featureSupported('ENABLE_EMAIL_OTP');

const AuthenticatorList = () => {
  const { t, i18n } = useTranslation();
  const { emitError } = useErrorContext();
  const navigateKeepSearch = useNavigateKeepSearch();
  const [searchParams] = useSearchParams();

  const { tenantId, userInfo, tenantInfo } = useSession();
  const onError = (e: ApiError) => {
    emitError(new AppError(e));
  };
  const { mutate: unenrollAuthenticator, isPending: isLoadingUnenrollAuthenticator } =
    useUnenrollAuthenticator(tenantId ?? '', { onError });
  const { mutate: deleteCredential, isPending: isPendingDeleteCredential } = useCredentialDelete({
    onError,
  });
  const { hasCamera, loadingCamera } = useHasCamera();
  const [authenticatorToRemove, setAuthenticatorToRemove] = useState<AuthenticatorToRemove>();
  // The remove confirm names the authenticator in both its title and its body.
  const removedAuthenticatorName = t(
    `${TRANSLATION_ROOT}.authenticators.${authenticatorToRemove?.factorType}`,
  );
  const [consentRevokeDialog, setConsentRevokeDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Gates the wrapper, so the adjacent-sibling gap rule only fires when a banner
  // is really in the DOM. The dismissal is mirrored in state so closing the banner
  // removes the wrapper immediately.
  const mfaStatus = useMfaEnrollmentStatus();
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(() =>
    isDismissed(SessionStorageKeys.MFA_ENROLLMENT_BANNER_DISMISSED),
  );

  const { data: passwordInfo } = usePasswordExpirationInfo();
  const [passwordAlertDismissed, setPasswordAlertDismissed] = useState(() =>
    isDismissed(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_MII),
  );
  const daysLeft = getDaysUntilExpiration(passwordInfo?.passwordExpiresAt);
  const isPasswordExpired = daysLeft === -1;
  const shouldShowPasswordExpiryRow =
    daysLeft !== null && (isPasswordExpired || daysLeft <= PASSWORD_EXPIRATION_WARNING_DAYS);
  const showPasswordAlert =
    daysLeft !== null &&
    daysLeft > 0 &&
    daysLeft <= PASSWORD_EXPIRATION_WARNING_DAYS &&
    !passwordAlertDismissed;

  const enableEpcs = ENABLE_EPCS && hasEntitlement(tenantInfo, Entitlement.EPCS);
  const epcsSetupDisabled = searchParams.get(SearchParams.DISABLE_EPCS)?.toLowerCase() === 'true';
  const offerEpcsSetup = enableEpcs && !epcsSetupDisabled;

  // The dismissal is state, not the openness: the re-consent flag arrives with the
  // landing-page query, so an `open` latched at mount would be `false` until the
  // response landed and would need an effect to correct. Nothing is persisted, so
  // a Cancel suppresses the prompt for this visit only.
  const [bioPromptDismissed, setBioPromptDismissed] = useState(false);

  // `from` travels along so the flow can return to this route.
  const goToBioAgreement = () =>
    navigateKeepSearch(
      `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_UPDATE_BIO_AGREEMENT}`,
      { state: { from: globalThis.location.pathname + globalThis.location.search } },
    );

  const {
    data: selfServiceLandingPageData,
    isError: isErrorSelfServiceLandingPageData,
    isLoading: isLoadingSelfServiceLandingPageData,
    refetch: refetchSelfServiceLandingPageData,
  } = useSelfServiceData();

  const authenticatorData: Record<string, AuthenticatorUiData> = useMemo(
    () => ({
      face: {
        text: t('self-service.auth-method.face'),
        icon: <Icon component={FaceIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_ENROLL_FACE.slice(1)),
      },
      password: {
        text: t('self-service.auth-method.password.authn-password'),
        icon: <Icon component={PasswordIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD.slice(1)),
      },
      imprivataId: {
        text: t('self-service.auth-method.imprivata-id.factor'),
        icon: <Icon component={imprivataIdIcon} />,
        onClick: () =>
          navigateKeepSearch(SelfServicePath.SELF_SERVICE_ENROLL_IMPRIVATA_ID.slice(1)),
      },
      otp: {
        text: t('self-service.auth-method.otp.otp-option'),
        icon: <Icon component={OtpIcon} />,
        onClick: () => navigateKeepSearch(SelfServicePath.SELF_SERVICE_OTP.slice(1)),
      },
    }),
    [navigateKeepSearch, t],
  );

  // consentType was removed on API V2. It is guaranteed to return only the impr.biometrics consent type here.
  const consentInfo = selfServiceLandingPageData?.consentInfos?.[0];

  const canRevokeBioConsent =
    !!consentInfo?.statusType && REVOCABLE_CONSENT_STATUSES.has(consentInfo.statusType);

  // Biometric re-consent (ME-11391), gated on face being enrolled whatever the
  // date says: re-consent exists to keep face authentication working, so with no
  // face there is nothing to keep working. The consent record outlives the
  // authenticator, so a lapsing consent with no face enrolled is what the backend
  // really returns after an unenroll.
  //
  // Gated here rather than on the prompt because this is the one derivation both
  // surfaces read. The row warning is already safe by construction — an unenrolled
  // face has no row to warn on — but that is where the rows come from, not a
  // statement about when re-consent is due.
  //
  // Enrollment is the presence of a FACE authenticator, not `isFaceEnabled`, which
  // means the opposite: it gates the *enroll* menu entry.
  const hasFaceAuthenticator = selfServiceLandingPageData?.authenticators?.some(
    auth => auth.factorType === FactorType.FACE,
  );
  const mustUpdateBioConsent =
    !!hasFaceAuthenticator && isBioReconsentDue(selfServiceLandingPageData?.consentInfos);
  const bioAgreementModalOpen = mustUpdateBioConsent && !bioPromptDismissed;

  const onSuccess = async () => {
    setAuthenticatorToRemove(undefined);
    await sleep(500);
    await refetchSelfServiceLandingPageData();
  };

  // Both warnings are date-driven off their own backend expiry, on a 14-day window.
  const rowWarningFor = (factorType: FactorType): RowWarning | undefined => {
    if (factorType === FactorType.FACE && mustUpdateBioConsent) {
      return { kind: ROW_WARNING.updateResidence };
    }
    if (factorType === FactorType.PASSWORD && shouldShowPasswordExpiryRow) {
      return isPasswordExpired
        ? { kind: ROW_WARNING.passwordExpired }
        : { kind: ROW_WARNING.passwordExpiring, daysLeft: daysLeft ?? 0 };
    }
    return undefined;
  };

  const authnDataSource = selfServiceLandingPageData?.authenticators
    ?.filter(a => supportedFactors.has(a.factorType))
    .map(auth => {
      const relativeTime = getRelativeTime(auth.lastUsedAt, i18n.language);
      return {
        key: auth.authenticatorId,
        factorType: auth.factorType,
        additionalText: relativeTime ? `${t('common.last-used')} ${relativeTime}` : '',
        shortName: auth.shortName,
        deviceModel: auth.deviceModel,
        onRemove: () =>
          setAuthenticatorToRemove({
            factorType: auth.factorType,
            remove: () => {
              unenrollAuthenticator(auth.authenticatorId, { onSuccess });
            },
          }),
        onRevokeConsent: canRevokeBioConsent ? () => setConsentRevokeDialog(true) : undefined,
        onEdit:
          auth.factorType === FactorType.PASSWORD
            ? () =>
                navigateKeepSearch(
                  `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
                )
            : undefined,
        epcs:
          enableEpcs &&
          auth.factorType === FactorType.IMPRIVATA_ID &&
          auth.assuranceType === AssuranceType.IAL2_ASSURED,
        onEpcsSetup:
          offerEpcsSetup &&
          auth.factorType === FactorType.IMPRIVATA_ID &&
          auth.assuranceType === AssuranceType.UNSPECIFIED
            ? () => {
                return navigateKeepSearch(
                  `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_EPCS}`,
                  {
                    state: {
                      authenticatorId: auth.authenticatorId,
                      authenticatorShortName: auth.shortName,
                      authenticatorDeviceModel: auth.deviceModel,
                    },
                  },
                );
              }
            : undefined,
        onUpdateBioAgreement: auth.factorType === FactorType.FACE ? goToBioAgreement : undefined,
        rowWarning: rowWarningFor(auth.factorType),
      };
    });

  const credsDataSource = selfServiceLandingPageData?.credentials?.map(cred => ({
    key: cred.credentialId,
    factorType: FactorType.LEARNED_CREDS,
    additionalText: cred.appDisplayName,
    onRemove: () =>
      setAuthenticatorToRemove({
        factorType: FactorType.LEARNED_CREDS,
        remove: () => {
          deleteCredential(cred.credentialId, { onSuccess });
        },
      }),
  }));

  const availableAuthMethods: Record<string, AuthenticatorUiData> = useMemo(() => {
    if (isLoadingSelfServiceLandingPageData || !selfServiceLandingPageData || loadingCamera) {
      return {};
    }

    const newMethods: Record<string, AuthenticatorUiData> = {};

    const IMPRIVATA_DOMAIN = 'imprivata.id';
    const hasImprivataId =
      userInfo?.email?.includes(IMPRIVATA_DOMAIN) ||
      userInfo?.userDisplayName?.includes(IMPRIVATA_DOMAIN);

    if (hasImprivataId) {
      newMethods.password = authenticatorData.password;
    }

    newMethods.imprivataId = authenticatorData.imprivataId;

    if (isFaceEnabled(selfServiceLandingPageData) && hasCamera) {
      newMethods.face = authenticatorData.face;
    }

    if (
      canEnrollOtp({
        enableEmailOtp: ENABLE_EMAIL_OTP,
        email: userInfo?.email,
        userDisplayName: userInfo?.userDisplayName,
        authenticators: selfServiceLandingPageData?.authenticators,
      })
    ) {
      newMethods.otp = authenticatorData.otp;
    }

    return newMethods;
  }, [
    isLoadingSelfServiceLandingPageData,
    selfServiceLandingPageData,
    hasCamera,
    userInfo?.email,
    userInfo?.userDisplayName,
    authenticatorData,
    loadingCamera,
  ]);

  const onRevokeConsent = async () => {
    setDialogLoading(true);
    try {
      await revokeConsent({
        consentRevokeList:
          consentInfo?.documentMetadataList.filter(
            d => d.documentPurpose !== DocumentPurpose.NOTICE,
          ) || [],
      });
      await sleep(500);
      setConsentRevokeDialog(false);
      await refetchSelfServiceLandingPageData();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setDialogLoading(false);
    }
  };

  if (isErrorSelfServiceLandingPageData) {
    return (
      <div className="flex flex-col justify-center p-4 align-center">
        <div className="mb-4 flex justify-center">
          <FullPageError />
        </div>
        <div className="mb-2 p-4">
          Unable to load authenticators. Try again after a few minutes.
        </div>
        <Button
          type="primary"
          onClick={() => refetchSelfServiceLandingPageData()}
          className="ant-btn ant-btn-primary"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const passwordAlertActions = (
    <ImprButton
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        navigateKeepSearch(
          `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
          { state: { from: globalThis.location.pathname + globalThis.location.search } },
        );
      }}
    >
      {t(`${TRANSLATION_ROOT_PASS}.update-button`)}
    </ImprButton>
  );

  return (
    <div className="authenticator-list-container">
      {showPasswordAlert && (
        <div className="password-expiration-alert">
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT_PASS}.title`, { count: daysLeft ?? 0 })}
            message={t(`${TRANSLATION_ROOT_PASS}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setPasswordAlertDismissed(true);
              markDismissed(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_MII);
            }}
          />
        </div>
      )}
      {mfaStatus && (!mfaBannerDismissed || mfaStatus.isPastDeadline) && (
        <div className="mfa-enrollment-alert-wrapper">
          <MfaEnrollmentBanner hideCta onDismiss={() => setMfaBannerDismissed(true)} />
        </div>
      )}
      {/*
        Biometric re-consent prompt (ME-11391). Opens on entry to this page while
        re-consent is required; the Face row's action bypasses it and enters the flow
        directly, since a user who clicked it has already made this choice.
      */}
      <ConfirmationModal
        open={bioAgreementModalOpen}
        onCancel={() => setBioPromptDismissed(true)}
        onOk={() => {
          setBioPromptDismissed(true);
          goToBioAgreement();
        }}
        graphic={TriangleAlert}
        graphicSeverity={GRAPHIC_SEVERITY.warning}
        title={t(`${TRANSLATION_ROOT}.bio-agreement-modal.title`)}
        content={t(`${TRANSLATION_ROOT}.bio-agreement-modal.content`)}
        okText={t('common.continue-button')}
        cancelText={t('common.cancel')}
        okPrimary
      />
      <ConfirmationModal
        open={!!authenticatorToRemove}
        onCancel={() => setAuthenticatorToRemove(undefined)}
        onOk={authenticatorToRemove?.remove}
        graphic={CircleHelp}
        title={t(`${TRANSLATION_ROOT}.remove-authenticator-title`, {
          authenticator: removedAuthenticatorName,
        })}
        content={t(`${TRANSLATION_ROOT}.confirm-remove-authenticator`, {
          authenticator: removedAuthenticatorName,
        })}
        okText={t(`${TRANSLATION_ROOT}.confirm-remove-face-btn`)}
        cancelText={t(`${TRANSLATION_ROOT}.cancel-remove-face`)}
        okLoading={isLoadingUnenrollAuthenticator || isPendingDeleteCredential}
      />
      <ConfirmationModal
        open={consentRevokeDialog}
        onCancel={() => setConsentRevokeDialog(false)}
        onOk={onRevokeConsent}
        graphic={CircleHelp}
        title={t(`${TRANSLATION_ROOT}.revoke-consent-title`)}
        content={t(`${TRANSLATION_ROOT}.revoke-consent-content`)}
        okText={t(`${TRANSLATION_ROOT}.confirm-revoke-consent`)}
        cancelText={t(`${TRANSLATION_ROOT}.cancel-revoke-consent`)}
        okLoading={dialogLoading}
      />
      <ImprList
        loading={isLoadingSelfServiceLandingPageData}
        columns={columns}
        dataSource={
          [...(authnDataSource ?? []), ...(credsDataSource ?? [])] as Record<string, unknown>[]
        }
        showHeader={false}
        pagination={false}
        enableSearch={false}
        headerExtraComponents={[
          <AuthMethodListHeader
            authenticatorsList={availableAuthMethods}
            key="table-extra-header"
          />,
        ]}
        locale={{
          emptyText: (
            <div className={'empty-list-container'}>
              <EmptyImage style={{ margin: '1rem' }} />
              <div className={'empty-list-container-title'}>
                {t('self-service.authenticator-list.empty-text')}
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default AuthenticatorList;
